import React, { SVGProps, createContext, useContext, useEffect, useRef, useState } from "react";
import { Layout, Menu, MenuItemProps, Spin } from "antd";
import { CheckSquareFilled, BorderOutlined } from "@ant-design/icons";
import { Outlet, useNavigate } from "react-router-dom";
import { clsx } from "clsx";
import { useUserState } from "../../common/UserContext";
import { useChatbotFlag } from "../demo/chatbotFlag";
import { useAppNav } from "../../common/AppNavContext";
import { isAdmin, isOnboarding, isSuperAdmin, isUser } from "../../types/UserGroup";
import { AppHeader } from "./AppHeader";
import { ReactComponent as VendorsIcon } from "../../assets/icons/svg/dataset.svg";
import { ReactComponent as NegotiationIcon } from "../../assets/icons/svg/group.svg";
import { ReactComponent as BriefsIcon } from "../../assets/icons/svg/briefs.svg";
import { ReactComponent as FormsIcon } from "../../assets/icons/svg/forms.svg";
import { ReactComponent as UsersIcon } from "../../assets/icons/svg/users.svg";
import { ReactComponent as SettingssIcon } from "../../assets/icons/svg/settings.svg";
import { ReactComponent as SuperAdminIcon } from "../../assets/icons/svg/admin.svg";
import { SvgIcon } from "../../common/components/SvgIcon";
import { usePageTracking } from "../../common/GoogleAnalytics/ReactRouter";
import classes from "./AppLayout.module.less";

const { Content, Sider } = Layout;

const MenuIconStyle: SVGProps<SVGSVGElement> = {
    width: 16,
    height: 16,
    fill: "#1890FF",
};

interface AppLayoutContextValue {
    getContentContainer: () => HTMLElement | null;
}

const AppLayoutContext = createContext<AppLayoutContextValue | null>(null);

export enum NavKey {
    Dashboard = "dashboard",
    Vendors = "vendors",
    Negotiations = "negotiations",
    BuyerBriefs = "buyerBriefs",
    Tactics = "tactics",
    Users = "users",
    Preferences = "preferences",
    SuperAdminMenu = "superAdminMenu",
    Orgs = "orgs",
    Impersonation = "impersonation",
    Industry = "industry",
    Notifications = "notifications",
    FeatureFlags = "featureFlags",
    Projects = "projects",
}

export const WithActiveNav: React.FC<{ navKey: NavKey }> = ({ navKey }) => {
    const { setActiveNav } = useAppNav();

    useEffect(() => {
        setActiveNav((pre) => pre.concat(navKey));

        return () => {
            setActiveNav((pre) => pre.filter((k) => k !== navKey));
        };
    }, [setActiveNav, navKey]);

    return <Outlet />;
};

const AppLayout: React.FC = () => {
    usePageTracking();

    const contentRef = useRef<HTMLElement>(null);

    const navigate = useNavigate();

    const [isSelected, setIsSelected] = useState(true);
    const { me } = useUserState();
    const [chatbotEnabled, setChatbotEnabled] = useChatbotFlag();
    const { highlightedNav, activeNav } = useAppNav();

    const isOnboardingUser = isOnboarding(me.roles);
    const isStandardUser = isUser(me.roles || []);
    const isAdminUser = isAdmin(me.roles || []);
    const isSuperAdminUser = isSuperAdmin(me.roles || []);

    const MenuItemWithHighlight = (props: MenuItemProps) => (
        <Menu.Item
            {...props}
            className={clsx(
                props.className,
                highlightedNav.includes(props.eventKey as NavKey) && classes.highlightedNav
            )}
        />
    );

    const projectsMenuItem = (
        <MenuItemWithHighlight
            key={NavKey.Projects}
            icon={<FormsIcon {...MenuIconStyle} />}
            onClick={() => navigate("/project")}
        >
            Procurement Hub
        </MenuItemWithHighlight>
    );

    const vendorsMenuItem = (
        <MenuItemWithHighlight
            key={NavKey.Vendors}
            icon={<VendorsIcon {...MenuIconStyle} />}
            onClick={() => navigate("/vendor")}
        >
            Vendors
        </MenuItemWithHighlight>
    );

    const negotiationIntelligenceMenuItem = (
        <MenuItemWithHighlight
            key={NavKey.Negotiations}
            icon={<NegotiationIcon {...MenuIconStyle} />}
            onClick={() => navigate("/")}
        >
            Negotiation intelligence
        </MenuItemWithHighlight>
    );

    const buyerBriefsMenuItem = (
        <MenuItemWithHighlight
            key={NavKey.BuyerBriefs}
            icon={<BriefsIcon {...MenuIconStyle} />}
            onClick={() => navigate("/supplier")}
        >
            Buyer Briefs
        </MenuItemWithHighlight>
    );

    const usersGroupsMenuItem = (
        <MenuItemWithHighlight
            key={NavKey.Users}
            icon={<UsersIcon {...MenuIconStyle} />}
            onClick={() => navigate("/admin/users")}
        >
            Users/Groups
        </MenuItemWithHighlight>
    );

    const preferencesMenuItem = (
        <MenuItemWithHighlight
            key={NavKey.Preferences}
            icon={<SettingssIcon {...MenuIconStyle} />}
            onClick={() => navigate("/preferences")}
        >
            Preferences
        </MenuItemWithHighlight>
    );

    const superAdminSubMenu = (
        <Menu.SubMenu
            key={NavKey.SuperAdminMenu}
            icon={<SuperAdminIcon {...MenuIconStyle} className="anticon" />}
            title="Super Admin"
        >
            <MenuItemWithHighlight key={NavKey.Orgs} onClick={() => navigate("/admin/orgs")}>
                Organizations
            </MenuItemWithHighlight>
            <MenuItemWithHighlight
                key={NavKey.Impersonation}
                onClick={() => navigate("/admin/impersonation")}
            >
                Impersonation
            </MenuItemWithHighlight>
            <MenuItemWithHighlight
                key={NavKey.Industry}
                onClick={() => navigate("/superadmin/industry")}
            >
                Industry
            </MenuItemWithHighlight>
            <MenuItemWithHighlight
                key={NavKey.Notifications}
                onClick={() => navigate("/notifications")}
            >
                Notifications
            </MenuItemWithHighlight>
            <MenuItemWithHighlight
                key={NavKey.FeatureFlags}
                onClick={() => navigate("/admin/flags")}
            >
                Feature Flags
            </MenuItemWithHighlight>
            <Menu.SubMenu title="Demos">
                <Menu.Item onClick={() => navigate("/superadmin/project")}>
                    Negotiation Playbook
                </Menu.Item>
                <Menu.Item onClick={() => navigate("/superadmin/demo/brief")}>
                    Buyer Briefs
                </Menu.Item>
                <Menu.Item
                    key="chatbot-toggle"
                    icon={
                        chatbotEnabled ? (
                            <CheckSquareFilled style={{ color: "#1890ff" }} />
                        ) : (
                            <BorderOutlined style={{ color: "#999" }} />
                        )
                    }
                    onClick={(info) => {
                        info.domEvent.stopPropagation();
                        setChatbotEnabled(!chatbotEnabled);
                    }}
                >
                    Chat bot
                </Menu.Item>
                <Menu.Item onClick={() => navigate("/superadmin/vendor/amazonwebservices6")}>
                    Vendor Details
                </Menu.Item>
                <Menu.Item onClick={() => navigate("/superadmin/demo/reports")}>
                    Reporting
                </Menu.Item>
                <Menu.Item onClick={() => navigate("/superadmin/demo/tail-spend")}>
                    Tail Spend
                </Menu.Item>
            </Menu.SubMenu>
        </Menu.SubMenu>
    );

    const menu = isOnboardingUser ? (
        <>{projectsMenuItem}</>
    ) : (
        <>
            {isStandardUser && negotiationIntelligenceMenuItem}
            {isStandardUser && projectsMenuItem}
            {isStandardUser && vendorsMenuItem}
            {isStandardUser && buyerBriefsMenuItem}
            {isAdminUser && usersGroupsMenuItem}
            <div className={classes.divider}></div>
            {preferencesMenuItem}
            {isSuperAdminUser && superAdminSubMenu}
        </>
    );

    const onCollapse = () => {
        setIsSelected(!isSelected);
    };
    return (
        <Spin spinning={me.email === undefined}>
            <AppLayoutContext.Provider value={{ getContentContainer: () => contentRef.current }}>
                <Layout className={classes.container}>
                    <AppHeader me={me} disabled={isOnboardingUser}></AppHeader>
                    <Layout>
                        <Sider
                            collapsible
                            collapsed={isSelected}
                            onCollapse={onCollapse}
                            theme="light"
                            className={classes.sider}
                            collapsedWidth={55}
                            trigger={
                                <SvgIcon name={isSelected ? "right" : "left"} color="#1890FF" />
                            }
                        >
                            <div className={classes.menuContainer}>
                                <Menu
                                    theme="light"
                                    mode="inline"
                                    className={clsx(
                                        classes.menu,
                                        isSelected && classes.menuCollapsed
                                    )}
                                    selectedKeys={activeNav}
                                    expandIcon={({ isOpen }) => (
                                        <SvgIcon
                                            name={isOpen ? "up" : "down"}
                                            className={"ant-menu-submenu-arrow"}
                                        />
                                    )}
                                >
                                    {menu}
                                </Menu>
                            </div>
                        </Sider>
                        <Content className={classes.stage} ref={contentRef}>
                            <div className={classes.content}>
                                <Outlet />
                            </div>
                        </Content>
                    </Layout>
                </Layout>
            </AppLayoutContext.Provider>
        </Spin>
    );
};

export const useAppLayoutContext = (): AppLayoutContextValue => {
    const context = useContext(AppLayoutContext);
    if (!context) {
        throw new Error("useAppLayoutContext must be used within a AppLayout");
    }
    return context;
};

export default AppLayout;
